import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { Confetti } from "./Confetti";
import type { DisciplineSlug } from "@jumpbean/shared";
import type { EarnedBadge } from "../stores/gameStore";

export interface BadgeModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** The badge that was earned */
  badge: EarnedBadge | null;
  /** The discipline for styling */
  discipline?: DisciplineSlug;
  /** Level number for the badge display */
  levelNumber?: number;
  /** Called when the modal should close */
  onClose: () => void;
}

/**
 * Celebratory modal displayed when a child earns a badge.
 * Features animations, confetti, and the earned badge prominently displayed.
 */
export function BadgeModal({
  isOpen,
  badge,
  discipline = "math",
  levelNumber = 1,
  onClose,
}: BadgeModalProps) {
  const [showBadge, setShowBadge] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // Animate in stages for dramatic effect
  useEffect(() => {
    if (isOpen) {
      // Reset states
      setShowBadge(false);
      setShowContent(false);

      // Stage 1: Show badge after brief delay
      const badgeTimer = setTimeout(() => {
        setShowBadge(true);
      }, 300);

      // Stage 2: Show content after badge
      const contentTimer = setTimeout(() => {
        setShowContent(true);
      }, 800);

      return () => {
        clearTimeout(badgeTimer);
        clearTimeout(contentTimer);
      };
    }
  }, [isOpen]);

  if (!isOpen || !badge) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="
          absolute inset-0
          bg-gradient-to-b from-magic-900/60 to-magic-950/80
          backdrop-blur-sm
          animate-[fadeIn_300ms_ease-out]
        "
        onClick={onClose}
      />

      {/* Confetti celebration */}
      <Confetti isActive={isOpen} count={100} duration={4000} />

      {/* Modal container */}
      <div
        className="
          relative
          w-full max-w-sm
          bg-gradient-to-b from-cloud via-sunshine-50 to-cloud
          rounded-3xl
          shadow-[0_20px_60px_rgba(0,0,0,0.3)]
          border-4 border-sunshine-300
          overflow-hidden
          animate-[badgeModalSlideIn_500ms_cubic-bezier(0.34,1.56,0.64,1)]
        "
      >
        {/* Decorative sparkle header */}
        <div className="absolute top-0 left-0 right-0 h-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-sunshine-200/50 to-transparent" />
          {/* Animated sparkles */}
          <SparkleDecor className="absolute top-4 left-8" delay={0} />
          <SparkleDecor className="absolute top-12 left-20" delay={200} />
          <SparkleDecor className="absolute top-6 right-12" delay={100} />
          <SparkleDecor className="absolute top-16 right-24" delay={300} />
        </div>

        {/* Content */}
        <div className="relative px-8 pt-8 pb-8 text-center">
          {/* Title */}
          <div
            className={`
              transition-all duration-500 transform
              ${showBadge ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"}
            `}
          >
            <h2 className="font-display font-bold text-display-md text-magic-600 mb-2">
              Amazing!
            </h2>
            <p className="font-display text-body-xl text-neutral-700 mb-8">
              You earned a new badge!
            </p>
          </div>

          {/* Badge display */}
          <div
            className={`
              flex justify-center mb-8
              transition-all duration-700 transform
              ${showBadge ? "scale-100 opacity-100" : "scale-50 opacity-0"}
            `}
          >
            <Badge
              discipline={discipline}
              level={levelNumber}
              isEarned
              size="lg"
              showLabel
              animate={showBadge}
            />
          </div>

          {/* Badge name */}
          <div
            className={`
              mb-8
              transition-all duration-500 transform delay-300
              ${showContent ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}
            `}
          >
            <h3 className="font-display font-bold text-display-sm text-neutral-900 mb-2">
              {badge.name}
            </h3>
            <p className="font-body text-body-lg text-neutral-600">
              Level {levelNumber} Complete!
            </p>
          </div>

          {/* Continue button */}
          <div
            className={`
              transition-all duration-500 transform delay-500
              ${showContent ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}
            `}
          >
            <Button
              variant="primary"
              size="lg"
              onClick={onClose}
              className="min-w-[180px]"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

/**
 * Animated sparkle decoration
 */
function SparkleDecor({
  className = "",
  delay = 0,
}: {
  className?: string;
  delay?: number;
}) {
  return (
    <div
      className={`w-4 h-4 ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-full h-full text-sunshine-400 animate-sparkle"
      >
        <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
      </svg>
    </div>
  );
}

// Add badge modal animation if not present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-badge-modal-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes badgeModalSlideIn {
        0% {
          opacity: 0;
          transform: scale(0.8) translateY(40px);
        }
        50% {
          transform: scale(1.05) translateY(-10px);
        }
        100% {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export default BadgeModal;
